import { withClickAnalytics } from "@asurion-core/eventualize-react";
import { Button, Text, Tooltip } from "@mantine/core";
import dayjs from "dayjs";
import { type JSX, useState } from "react";
import { useTimer } from "react-timer-hook";

import type { VoiceTask } from "@/sdk";

import { useAgentSdk } from "@/sdk";
import { useAsyncLoading } from "@/shared-utils";

import { handleEndCall } from "../../eventHandlers";
import { useControlsStore, withControlsLock } from "../controls.store";

const AnalyticButton = withClickAnalytics(Button<"button">, "Click");

/**
 * This button is used to cancel a pending call that has not yet connected
 * @param task - the active voice task associated with the call
 */
export function CancelPendingCallButton({ task }: { task: VoiceTask }): JSX.Element | null {
    const agentSdk = useAgentSdk();
    const { controlLock } = useControlsStore();

    const [allowCancel, setAllowCancel] = useState<boolean>(false);

    /** delay in seconds to wait until showing the cancel button */
    const defaultDelay = 10;

    useTimer({
        expiryTimestamp: dayjs().add(defaultDelay, "seconds").toDate(),
        autoStart: true,
        onExpire: () => setAllowCancel(true),
    });

    const [isLoading, endCall] = useAsyncLoading(withControlsLock(async () => await handleEndCall(agentSdk, task)));

    return allowCancel ? (
        <Tooltip label="Cancel pending call" position="bottom" zIndex={999}>
            <AnalyticButton
                analytics={{
                    actionId: "CancelCallButton",
                }}
                aria-label="Cancel Call"
                data-testid="cancel-call-controls-leave"
                disabled={isLoading || task.status !== "assigned" || controlLock}
                loading={isLoading || controlLock}
                onClick={() => void endCall()}
                radius="xl"
                size="xs"
                color="red.9"
            >
                <Text size="xs">Cancel</Text>
            </AnalyticButton>
        </Tooltip>
    ) : null;
}
